<template>
  <div class="qr-decrypt-container">
    <!-- 解密状态 -->
    <div class="status-section mb-6">
      <div class="flex items-center justify-center">
        <div class="text-center">
          <div class="text-lg font-semibold mb-2" :class="statusColor">
            {{ statusText }}
          </div>
          <!-- <div v-if="pollingCount > 0" class="text-sm text-gray-500 mt-1">
            已轮询 {{ pollingCount }} 次
          </div> -->
        </div>
      </div>
    </div>

    <!-- 扫码成功提示（当用户已扫码但未确认时显示） -->
    <div v-if="currentStatus === DECRYPT_STATUS.SCANNED" class="scanned-notice mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <a-icon type="check-circle" class="text-orange-500 text-2xl" />
        </div>
        <div class="ml-3">
          <h4 class="text-orange-800 font-semibold">扫码成功！</h4>
          <p class="text-orange-600 text-sm mt-1">请在标证通APP上确认解密操作，完成后将自动跳转</p>
        </div>
      </div>
    </div>

    <!-- 二维码展示区域（修复 v-if/v-else 语法） -->
    <div v-if="showQRCode" class="qr-code-section mb-8">
      <div class="flex justify-center">
        <div class="qr-code-wrapper p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div class="qr-code-container relative">
            <!-- 加载状态 -->
            <div v-if="isGenerating" class="flex items-center justify-center w-64 h-64">
              <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              <span class="ml-2 text-sm text-gray-500">正在生成二维码...</span>
            </div>
            
            <div v-else class="qr-code-display">
              <div v-if="mockQRContent" class="mx-auto">
                <!-- 全国互认版本：如果是 base64 图片，直接显示 img -->
                <img 
                  v-if="mockQRContent.startsWith('data:image')" 
                  :src="mockQRContent" 
                  class="w-64 h-64"
                  alt="二维码"
                />
                <!-- 标证通版本：如果是 url，用 qrcode.vue 生成 -->
                <qrcode-vue
                  v-else
                  :value="mockQRContent"
                  :size="256"
                  level="H"
                />
                <div class="mt-2 text-center">
                  <div class="text-xs text-gray-500">
                    {{ caMethod === 'national' ? '请使用全国CA互认平台扫码' : '请使用标证通APP扫码' }}
                  </div>
                </div>
              </div>
              
              <!-- 无网址时的占位（v-else 匹配上面的 v-if） -->
              <div v-else class="qr-code-placeholder flex items-center justify-center w-64 h-64 bg-gray-100 border-2 border-dashed border-gray-300 rounded">
                <div class="text-center">
                  <div class="text-4xl mb-2">📱</div>
                  <div class="text-gray-600 font-medium">标证通解密二维码</div>
                  <div class="text-sm text-gray-500 mt-1">等待生成二维码</div>
                </div>
              </div>
              
              <!-- 过期遮罩 -->
              <div 
                v-if="currentStatus === DECRYPT_STATUS.EXPIRED && mockQRContent" 
                class="qr-code-expired-overlay absolute inset-0 bg-red-500 bg-opacity-90 flex items-center justify-center rounded"
              >
                <div class="text-white text-center p-4">
                  <div class="text-xl font-bold mb-2">二维码已过期</div>
                  <div class="text-sm">请重新生成二维码</div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 二维码信息 -->
          <div class="mt-4 text-center">
            <!-- <div class="text-sm text-gray-600">
              二维码ID: <span class="font-mono">{{ qrcodeid || '未生成' }}</span>
            </div> -->
            <!-- <div v-if="mockQRContent" class="text-xs text-gray-400 mt-1 break-all max-w-xs mx-auto">
              生成网址: {{ mockQRContent }}
            </div> -->
            <div class="text-xs text-gray-500 mt-1">
              生成时间: {{ generateTime || '--' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons flex justify-center space-x-4">
      <button 
        v-if="showBackButton"
        @click="handleBack"
        class="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
      >
        上一步
      </button>
      
      <button 
        v-if="showRetryButton"
        @click="handleRetry"
        :disabled="isGenerating"
        class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ isGenerating ? '正在生成...' : '重新生成二维码' }}
      </button>
      
      <button 
        v-if="showStartButton"
        @click="startDecrypt"
        :disabled="isGenerating"
        class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {{ isGenerating ? '正在生成二维码...' : '开始解密' }}
      </button>
    </div>

    <!-- 状态说明 -->
    <div class="status-info mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div class="flex items-start">
        <div class="text-blue-500 text-lg mr-2 mt-0.5">ℹ️</div>
        <div>
          <p class="text-blue-800 font-medium">解密状态说明：</p>
          <ul class="text-blue-600 text-sm mt-1 space-y-1">
            <li>• <span class="font-medium">生成二维码</span> - 系统正在创建解密二维码</li>
            <li>• <span class="font-medium">等待扫码</span> - 请使用标证通APP扫描上方二维码</li>
            <li>• <span class="font-medium">APP确认</span> - 请在APP上确认解密操作</li>
            <li>• <span class="font-medium">解密成功</span> - 获取解密结果，自动进入下一步</li>
            <li>• <span class="font-medium">二维码失效</span> - 二维码过期，需要重新生成</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- 调试信息 -->
    <!-- <div v-if="isDevelopment" class="debug-info mt-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
      <div class="text-sm font-medium text-gray-700 mb-2">调试信息（Mock数据）</div>
      <div class="text-xs text-gray-600 space-y-1">
        <div>当前状态: {{ currentStatus }}</div>
        <div>轮询次数: {{ pollingCount }}</div>
        <div>二维码ID: {{ qrcodeid }}</div>
        <div>生成网址: {{ mockQRContent }}</div>
        <div class="mt-2">
          <div class="text-xs text-gray-500 mb-2">点击按钮设置下次轮询返回的状态：</div>
          <button @click="setDebugStatus('00')" class="px-2 py-1 text-xs border border-gray-300 rounded mr-2">00-等待扫码</button>
          <button @click="setDebugStatus('01')" class="px-2 py-1 text-xs border border-gray-300 rounded mr-2">01-已扫码待确认</button>
          <button @click="setDebugStatus('02')" class="px-2 py-1 text-xs border border-gray-300 rounded mr-2">02-解密成功</button>
          <button @click="setDebugStatus('03')" class="px-2 py-1 text-xs border border-gray-300 rounded">03-二维码过期</button>
        </div>
      </div>
    </div> -->
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue'
import { AnQingCreateQR, AnQingQueryQR, AnQingCreateQRNational, AnQingQueryQRNational } from '@/api/ggb/linhai'

// 解密状态枚举
const DECRYPT_STATUS = {
  INIT: 'init',
  GENERATING: 'generating',
  QR_SHOWING: 'qr_showing',
  SCANNED: 'scanned',
  CONFIRMED: 'confirmed',
  EXPIRED: 'expired',
  ERROR: 'error'
};

export default {
  name: 'QRCodeDecrypt',
  components: {
    QrcodeVue
  },
  props: {
    encryptedData: {
      type: String,
      default: 'MIGBAiEAlGBvqfgoKmiUem7HeCJnUMPIlDR3yv93BHVyIZlGsysCICczsgedVG/mPONefAzcY9R2qhVoIcRQDV32+Vt5nFqcBCCGIodZUwTxIWQzUvnsd50RrSwAeOWCc9PH9VGueN9M5QQYM77CawKPzh4ut8awI2xu9x4aj6wH004r'
    },
    caMethod: {
      type: String,
      default: 'bzt'
    },
    digitalEnvelope: {
      type: String,
      default: ''
    },
    toubaono: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      DECRYPT_STATUS: DECRYPT_STATUS,
      currentStatus: DECRYPT_STATUS.INIT,
      qrcodeid: null,
      generateTime: null,
      pollingInterval: null,
      pollingCount: 0,
      isGenerating: false,
      mockQRContent: '',
      retryCount: 0,
      maxRetries: 3,
      mockSequenceIndex: 0,
      // Mock 状态流转序列：
      // '00' = 等待扫码 (QR_SHOWING)
      // '01' = 已扫码，等待APP确认 (SCANNED)  
      // '02' = 用户已确认，解密中 -> 解密成功 (CONFIRMED)
      // '03' = 二维码过期/失败 (EXPIRED)
      isDevelopment: true,
      // 轮询间隔（毫秒）：模拟真实接口延迟
      pollingIntervalMs: 3000,
      // 外置调试状态控制 - 用于手动控制每次轮询返回的状态
      // '00'=等待扫码, '01'=已扫码, '02'=成功, '03'=过期, null=保持当前状态不变
      debugNextStatus: null
    };
  },
  computed: {
    statusText() {
      const map = {
        [DECRYPT_STATUS.INIT]: '准备开始解密',
        [DECRYPT_STATUS.GENERATING]: '正在生成二维码...',
        [DECRYPT_STATUS.QR_SHOWING]: '请使用标证通APP扫描二维码',
        [DECRYPT_STATUS.SCANNED]: '扫码成功！请在标证通APP上确认解密操作',
        [DECRYPT_STATUS.CONFIRMED]: '解密成功！正在跳转...',
        [DECRYPT_STATUS.EXPIRED]: '二维码已失效，请重新生成',
        [DECRYPT_STATUS.ERROR]: '解密失败，请重试'
      };
      return map[this.currentStatus] || '未知状态';
    },
    statusColor() {
      const map = {
        [DECRYPT_STATUS.INIT]: 'text-gray-600',
        [DECRYPT_STATUS.GENERATING]: 'text-blue-600',
        [DECRYPT_STATUS.QR_SHOWING]: 'text-blue-600',
        [DECRYPT_STATUS.SCANNED]: 'text-orange-500',
        [DECRYPT_STATUS.CONFIRMED]: 'text-green-600',
        [DECRYPT_STATUS.EXPIRED]: 'text-red-600',
        [DECRYPT_STATUS.ERROR]: 'text-red-600'
      };
      return map[this.currentStatus] || 'text-gray-600';
    },
    showQRCode() {
      return [
        DECRYPT_STATUS.QR_SHOWING,
        DECRYPT_STATUS.SCANNED,
        DECRYPT_STATUS.EXPIRED
      ].includes(this.currentStatus);
    },
    showBackButton() {
      return this.currentStatus !== DECRYPT_STATUS.CONFIRMED;
    },
    showRetryButton() {
      return this.currentStatus === DECRYPT_STATUS.EXPIRED;
    },
    showStartButton() {
      return this.currentStatus === DECRYPT_STATUS.INIT;
    }
  },
  beforeDestroy() {
    this.clearIntervals();
  },
  methods: {
    async startDecrypt() {
      this.currentStatus = DECRYPT_STATUS.GENERATING;
      this.isGenerating = true;
      
      // console.log('🔑 QRCodeDecrypt - 接收到的 digitalEnvelope:', this.digitalEnvelope);
      // console.log('🔑 QRCodeDecrypt - props 完整数据:', this.$props);
      
      try {
        let res;
        // 根据 caMethod 调用不同的接口，传递 digitalEnvelope
        if (this.caMethod === 'national') {
          // 全国互认版本
          const params = { digitalenvelope: this.digitalEnvelope };
          // console.log('📤 全国互认版本请求参数:', params);
          res = await AnQingCreateQRNational(params);
          // console.log("全国互认版本创建二维码响应:", res);
          this.qrcodeid = res.custom.qrcodeid;
          // 全国互认版本返回的是 qrimagebase64，需要处理一下
          if (res.custom.qrimagebase64) {
            this.mockQRContent = 'data:image/png;base64,' + res.custom.qrimagebase64;
          }
        } else {
          // 标证通版本
          const params = { digitalenvelope: this.digitalEnvelope };
          // console.log('📤 标证通版本请求参数:', params);
          res = await AnQingCreateQR(params);
          // console.log("标证通版本创建二维码响应:", res);
          this.qrcodeid = res.custom.qrcodeid;
          this.mockQRContent = res.custom.content;
          // todo:需要判断status吗?
        }
        this.generateTime = new Date().toLocaleTimeString();
        this.currentStatus = DECRYPT_STATUS.QR_SHOWING;
        this.isGenerating = false;
        this.startPolling();
      } catch (error) {
        console.error('生成二维码失败:', error);
        this.currentStatus = DECRYPT_STATUS.ERROR;
        this.isGenerating = false;
      }
    },
    startPolling() {
      this.pollingCount = 0;
      this.clearPolling();
      this.pollingInterval = setInterval(async () => {
        this.pollingCount++;
        // 调试日志
        // console.log('[调试] 轮询第', this.pollingCount, '次, 当前状态:', this.currentStatus);
        try {
          // 调用后端接口查询扫码状态
          let res;
          if (this.caMethod === 'national') {
            // 全国互认版本
            res = await AnQingQueryQRNational({ qrcodeid: this.qrcodeid, toubaono: this.toubaono, caMethod: 3 });
          } else {
            // 标证通版本
            res = await AnQingQueryQR({ qrcodeid: this.qrcodeid, toubaono: this.toubaono, caMethod: 2 });
          }
          // 根据返回状态更新
          // 接口实际返回：{ "status": { ... }, "custom": { "qrstatus": "02", ... } }
          let qrstatus = '00';
          if (res.custom && res.custom.qrstatus) {
            qrstatus = res.custom.qrstatus;
          }
          switch(qrstatus) {
            case '00':
              this.currentStatus = DECRYPT_STATUS.QR_SHOWING;
              break;
            case '01':
              this.currentStatus = DECRYPT_STATUS.SCANNED;
              break;
            case '02':
              this.clearPolling();
              this.currentStatus = DECRYPT_STATUS.CONFIRMED;
              // 解密成功，后端已经提取了 org，这里直接通知成功
              this.handleDecryptSuccess('success');
              break;
            case '03':
              this.clearPolling();
              this.currentStatus = DECRYPT_STATUS.EXPIRED;
              break;
          }
        } catch (error) {
          console.error('查询状态失败:', error);
        }
      }, this.pollingIntervalMs);
    },
    handleDecryptSuccess(decryptedKey) {
      console.log('解密成功:', decryptedKey);
      this.$emit('decrypt-success', {
        decryptedKey: decryptedKey,
        caMethod: this.caMethod
      });
    },
    handleQRExpired() {
      this.clearIntervals();
      this.currentStatus = DECRYPT_STATUS.EXPIRED;
      this.retryCount++;
      if (this.retryCount > this.maxRetries) {
        alert('已达到最大重试次数，请返回重新选择解密方式');
      }
    },
    handleRetry() {
      if (this.retryCount > this.maxRetries) {
        alert('已达到最大重试次数，请返回重新选择解密方式');
        return;
      }
      this.mockSequenceIndex = 0;
      this.startDecrypt();
    },
    handleBack() {
      this.clearIntervals();
      this.currentStatus = DECRYPT_STATUS.INIT;
      this.qrcodeid = null;
      this.mockQRContent = '';
      this.generateTime = null;
      this.$emit('go-back');
    },
    clearIntervals() {
      this.clearPolling();
    },
    clearPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    },
    // 调试方法 - 设置下次轮询返回的状态（用于模拟后端API返回）
    setDebugStatus(status) {
      const statusMap = {
        '00': '等待扫码（二维码已生成，等待用户扫描）',
        '01': '已扫码，等待APP确认（用户已扫描，需在标证通APP上确认解密）',
        '02': '解密成功（用户已确认，解密完成）',
        '03': '二维码过期（二维码已失效，需重新生成）'
      };
      this.debugNextStatus = status;
      this.$message.info(`已设置下次轮询返回: [${status}] ${statusMap[status]}`);
    }
  }
};
</script>

<style scoped>
.qr-decrypt-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.status-section { margin-bottom: 1.5rem; }
.qr-code-section { margin-bottom: 2rem; }

.qr-code-wrapper {
  position: relative;
  max-width: 300px;
}

.qr-code-container {
  min-height: 256px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qr-code-display {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 适配 qrcode.vue 生成的二维码样式 */
.qr-code-display >>> canvas {
  width: 256px !important;
  height: 256px !important;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.qr-code-placeholder {
  width: 256px;
  height: 256px;
  border-radius: 4px;
}

.qr-code-expired-overlay {
  width: 256px;
  height: 256px;
  border-radius: 4px;
  z-index: 10;
  position: absolute;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
}

.action-buttons { margin-bottom: 2rem; }
.status-info { margin-top: 2rem; }
.debug-info { margin-top: 1.5rem; }

/* 加载动画 */
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 响应式 */
@media (max-width: 768px) {
  .qr-decrypt-container { padding: 10px; }
  .qr-code-wrapper { max-width: 100%; }
}
</style>
